import { CONVERTING_STATUS } from '@src/constants/project';

/**
 * Selectors that read the block map but return a PRIMITIVE.
 *
 * Editing one character replaces `state.project` and its `multiBlock.blocks`,
 * so a component subscribing to either identity re-renders on every keystroke —
 * even when it only wanted a yes/no answer about the blocks. Deriving that
 * answer inside the selector means `useSelector`'s reference check compares
 * booleans instead, and the component wakes only when the answer actually flips.
 *
 * The scan is the same work the components already did while rendering; it just
 * moves off the render path. It now runs per dispatch rather than per render,
 * which is the cheaper side of the trade for a project with many blocks.
 */
const blocksOf = (state) => state.project.multiBlock?.blocks || {};

/**
 * Is any block currently being synthesized?
 *
 * Per-block create-audio never sets the project-wide synthesis flags — only the
 * block's own status — so callers that gate on "something is converting" have to
 * check this too.
 */
export const selectHasSynthesizingBlock = (state) =>
  Object.values(blocksOf(state)).some(
    (block) => block?.status === CONVERTING_STATUS.IN_PROGRESS,
  );

/**
 * Have all blocks that carry text been synthesized successfully? False when no
 * block has text, so an empty project never reads as "done".
 */
export const selectEveryTextBlockSynthesized = (state) => {
  const withText = Object.values(blocksOf(state)).filter(
    (block) => block?.text,
  );
  return (
    withText.length > 0 &&
    withText.every((block) => block?.status === CONVERTING_STATUS.SUCCESS)
  );
};
